// src/lib/fileUtils.ts

/**
 * Get Monaco Editor language from file extension
 */
export function getLanguageFromExtension(filePath: string): string {
  const extension = filePath.split(".").pop()?.toLowerCase() || "";

  // Map file extensions to Monaco Editor language IDs
  const languageMap: Record<string, string> = {
    // Web
    html: "html",
    htm: "html",
    css: "css",
    scss: "scss",
    sass: "scss",
    less: "less",
    js: "javascript",
    jsx: "javascript",
    ts: "typescript",
    tsx: "typescript",
    json: "json",
    jsonc: "json",

    // Python
    py: "python",
    pyw: "python",
    ipynb: "json", // Jupyter notebooks are JSON files

    // Data formats
    csv: "plaintext",
    tsv: "plaintext",
    yml: "yaml",
    yaml: "yaml",
    xml: "xml",
    toml: "ini", // Monaco doesn't have TOML support built-in

    // Markdown and text
    md: "markdown",
    markdown: "markdown",
    txt: "plaintext",
    text: "plaintext",
    rst: "plaintext",

    // Config files
    ini: "ini",
    cfg: "ini",
    conf: "ini",
    env: "plaintext",

    // Shell and scripts
    sh: "shell",
    bash: "shell",
    zsh: "shell",
    bat: "bat",
    cmd: "bat",
    ps1: "powershell",

    // SQL
    sql: "sql",

    // Docker
    dockerfile: "dockerfile",

    // Git
    gitignore: "plaintext",
    gitattributes: "plaintext",

    // Other
    log: "plaintext",
  };

  return languageMap[extension] || "plaintext";
}

/**
 * Check if a file is binary based on its extension
 */
export function isBinaryFile(filePath: string): boolean {
  const extension = filePath.split(".").pop()?.toLowerCase() || "";

  const binaryExtensions = [
    // Images
    "png",
    "jpg",
    "jpeg",
    "gif",
    "bmp",
    "webp",
    "ico",
    "svg",
    "tif",
    "tiff",

    // Documents
    "pdf",
    "doc",
    "docx",
    "xls",
    "xlsx",
    "ppt",
    "pptx",

    // Archives
    "zip",
    "rar",
    "tar",
    "gz",
    "7z",

    // Audio/Video
    "mp3",
    "mp4",
    "wav",
    "ogg",
    "avi",
    "mov",
    "mkv",

    // Other
    "exe",
    "dll",
    "so",
    "bin",
    "dat",
  ];

  return binaryExtensions.includes(extension);
}

/**
 * Get a file icon based on its extension
 */
export function getFileIcon(filePath: string): string {
  const extension = filePath.split(".").pop()?.toLowerCase() || "";
  const fileName = filePath.split("/").pop()?.toLowerCase() || "";

  // Special files by name
  if (fileName === "package.json") return "📦";
  if (fileName === "readme.md") return "📝";
  if (fileName === "license") return "⚖️";
  if (fileName === "dockerfile") return "🐳";
  if (fileName === ".gitignore" || fileName === ".gitattributes") return "🔖";

  // By extension
  const iconMap: Record<string, string> = {
    // Web
    html: "🌐",
    htm: "🌐",
    css: "🎨",
    scss: "🎨",
    sass: "🎨",
    less: "🎨",
    js: "📜",
    jsx: "⚛️",
    ts: "🔷",
    tsx: "⚛️",
    json: "📋",

    // Python
    py: "🐍",
    pyw: "🐍",
    ipynb: "📊",

    // Data formats
    csv: "📊",
    tsv: "📊",
    yml: "⚙️",
    yaml: "⚙️",
    xml: "📃",
    toml: "⚙️",

    // Markdown and text
    md: "📝",
    markdown: "📝",
    txt: "📄",
    text: "📄",

    // Images
    png: "🖼️",
    jpg: "🖼️",
    jpeg: "🖼️",
    gif: "🖼️",
    bmp: "🖼️",
    webp: "🖼️",
    ico: "🖼️",
    svg: "🖼️",

    // Config files
    ini: "⚙️",
    cfg: "⚙️",
    conf: "⚙️",
    env: "⚙️",

    // Shell and scripts
    sh: "💻",
    bash: "💻",
    zsh: "💻",
    bat: "💻",
    cmd: "💻",
    ps1: "💻",

    // SQL
    sql: "🗃️",
  };

  return iconMap[extension] || "📄";
}

/**
 * Normalize a path to use forward slashes and remove leading/trailing slashes
 */
export function normalizePath(path: string): string {
  // Replace backslashes with forward slashes
  let normalized = path.replace(/\\/g, "/");

  // Remove multiple consecutive slashes
  normalized = normalized.replace(/\/+/g, "/");

  // Remove trailing slash if present
  if (normalized.endsWith("/") && normalized !== "/") {
    normalized = normalized.slice(0, -1);
  }

  return normalized;
}

/**
 * Join path segments, ensuring proper slash handling
 */
export function joinPaths(...paths: string[]): string {
  return normalizePath(paths.join("/"));
}

/**
 * Get the parent directory of a path
 */
export function getParentPath(path: string): string {
  const normalized = normalizePath(path);
  const lastSlashIndex = normalized.lastIndexOf("/");

  if (lastSlashIndex <= 0) {
    // Root path or no slashes
    return "/";
  }

  return normalized.substring(0, lastSlashIndex);
}

/**
 * Get the file name from a path
 */
export function getFileName(path: string): string {
  const normalized = normalizePath(path);
  const parts = normalized.split("/");
  return parts[parts.length - 1] || "";
}
